/**
 * DashboardChart — enterprise analytics wrapper for Recharts.
 *
 * Drill-down works by internally re-fetching via `queryFn` when drill state
 * changes. Pass `queryFn` + `baseParams` for live data; otherwise pass `data`
 * for static/parent-controlled data.
 */

import React, { useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceLine, Brush,
  ResponsiveContainer,
} from 'recharts';
import { X, MessageSquarePlus } from 'lucide-react';

import { useDashboard, ChartType, SortBy } from '../../context/DashboardContext';
import { useDrillDown }   from '../../hooks/useDrillDown';
import { useCrossFilter } from '../../hooks/useCrossFilter';
import { useForecast }    from '../../hooks/useForecast';
import { useAnnotations } from '../../hooks/useAnnotations';
import { useExport }      from '../../hooks/useExport';

import ChartToolbar    from './ChartToolbar';
import DrillBreadcrumb from './DrillBreadcrumb';
import EnrichedTooltip from './EnrichedTooltip';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SeriesConfig {
  key:   string;
  name:  string;
  color: string;
}

export interface DrillLevel {
  label:    string;   // breadcrumb label shown on hover / "All → Monthly → Weekly"
  paramKey: string;   // query param added when drilling (e.g. "month", "week")
}

export interface DashboardChartConfig {
  enableDrillDown?:   boolean;
  enableCrossFilter?: boolean;
  enableForecast?:    boolean;
  enableBenchmark?:   boolean;
  enableBrush?:       boolean;
  enableAnnotations?: boolean;
  enableLinkedPage?:  boolean;
  benchmarkValue?:    number;
  benchmarkLabel?:    string;
  linkedPage?:        string;
  drillLevels?:       DrillLevel[];
}

interface DashboardChartProps {
  chartId:          string;
  title:            string;
  subtitle?:        string;
  // Static data mode (no drill re-fetch)
  data?:            Record<string, unknown>[];
  // Live data mode — component fetches internally; drill triggers refetch
  queryFn?:         (params: Record<string, unknown>) => Promise<Record<string, unknown>[]>;
  baseParams?:      Record<string, unknown>;   // dateRange, labId from parent
  xDataKey:         string;
  series:           SeriesConfig[];
  height?:          number;
  config?:          DashboardChartConfig;
  isLoading?:       boolean;
  className?:       string;
  defaultChartType?: ChartType;  // used when no persisted preference exists
  /** Called when user clicks a bar — receives the row data and bar index */
  onBarClick?:      (data: unknown, index: number) => void;
}

// ── Sort / top-N helpers ──────────────────────────────────────────────────────

function applySort(data: Record<string, unknown>[], by: SortBy, pk: string) {
  const d = [...data];
  if (by === 'value-desc') return d.sort((a, b) => Number(b[pk] ?? 0) - Number(a[pk] ?? 0));
  if (by === 'value-asc')  return d.sort((a, b) => Number(a[pk] ?? 0) - Number(b[pk] ?? 0));
  if (by === 'name-asc')   return d.sort((a, b) => String(a.label ?? '').localeCompare(String(b.label ?? '')));
  return d;
}

function applyTopN(data: Record<string, unknown>[], n: number | null, pk: string) {
  if (!n) return data;
  return [...data].sort((a, b) => Number(b[pk] ?? 0) - Number(a[pk] ?? 0)).slice(0, n);
}

// ── Data table ────────────────────────────────────────────────────────────────

function DataTableView({ data, xDataKey, series }: {
  data: Record<string, unknown>[];
  xDataKey: string;
  series: SeriesConfig[];
}) {
  const [sortKey, setSortKey] = useState(xDataKey);
  const [dir,     setDir    ] = useState<'asc'|'desc'>('asc');
  const [page,    setPage   ] = useState(0);
  const PG = 10;

  const sorted = useMemo(() => {
    const copy = [...data];
    copy.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      const num = typeof av === 'number' && typeof bv === 'number';
      return dir === 'asc'
        ? num ? av - bv : String(av).localeCompare(String(bv))
        : num ? bv - av : String(bv).localeCompare(String(av));
    });
    return copy;
  }, [data, sortKey, dir]);

  const paged = sorted.slice(page * PG, (page + 1) * PG);
  const pages = Math.ceil(sorted.length / PG);

  const toggle = (k: string) => {
    if (sortKey === k) setDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setDir('desc'); }
  };

  const th: React.CSSProperties = {
    padding: '6px 10px', fontSize: 10.5, fontWeight: 700, color: '#5a5a6e',
    background: '#f7f5f2', textAlign: 'left', cursor: 'pointer',
    borderBottom: '1px solid rgba(0,0,0,0.1)', whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = {
    padding: '6px 10px', fontSize: 11.5, color: '#374151',
    borderBottom: '1px solid rgba(0,0,0,0.04)',
  };

  const cols = [
    { key: xDataKey, name: xDataKey, color: '' },
    ...series.map((s) => ({ key: s.key, name: s.name, color: s.color })),
  ];

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.key} style={th} onClick={() => toggle(c.key)}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {c.color && <span style={{ width: 7, height: 7, borderRadius: 2, background: c.color, display: 'inline-block' }} />}
                  {c.name}{sortKey === c.key ? (dir === 'asc' ? ' ↑' : ' ↓') : ''}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paged.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
              {cols.map((c) => {
                const v    = row[c.key];
                const prev = i > 0 ? Number(paged[i - 1]?.[c.key] ?? 0) : null;
                const isN  = typeof v === 'number';
                const delta = isN && prev !== null && prev !== 0
                  ? ((v - prev) / prev) * 100 : null;
                return (
                  <td key={c.key} style={td}>
                    <span style={{ fontWeight: c.color ? 600 : 400 }}>
                      {isN ? v.toLocaleString() : String(v ?? '')}
                    </span>
                    {delta !== null && c.color && (
                      <span style={{
                        marginLeft: 6, fontSize: 9.5, fontWeight: 600,
                        padding: '1px 5px', borderRadius: 100,
                        background: delta > 0 ? '#F0FDF4' : delta < 0 ? '#FEF2F2' : 'rgba(0,0,0,0.04)',
                        color: delta > 0 ? '#16A34A' : delta < 0 ? '#DC2626' : '#94A3B8',
                      }}>
                        {delta > 0 ? '+' : ''}{delta.toFixed(1)}%
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginTop: 8 }}>
          {Array.from({ length: pages }, (_, i) => (
            <button key={i} onClick={() => setPage(i)} style={{
              width: 22, height: 22, borderRadius: 5, border: '1px solid rgba(0,0,0,0.1)',
              background: page === i ? '#b07d1a' : '#fff',
              color: page === i ? '#fff' : '#5a5a6e',
              fontSize: 10.5, cursor: 'pointer',
            }}>{i + 1}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Annotation input overlay ──────────────────────────────────────────────────

function AnnotationInput({ xValue, text, onChange, onCommit, onCancel }: {
  xValue: string; text: string;
  onChange: (t: string) => void;
  onCommit: () => void; onCancel: () => void;
}) {
  return (
    <div style={{
      position: 'absolute', top: '30%', left: '50%', transform: 'translateX(-50%)',
      zIndex: 200, background: '#fff', border: '1px solid rgba(0,0,0,0.1)',
      borderRadius: 10, padding: '12px 14px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 240,
    }}>
      <p style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>
        <MessageSquarePlus size={12} style={{ display: 'inline', marginRight: 4 }} />
        Note at <em>{xValue}</em>
      </p>
      <textarea autoFocus value={text} onChange={(e) => onChange(e.target.value)}
        placeholder="Type your note…" rows={2}
        style={{
          width: '100%', fontSize: 12, padding: '6px 8px',
          borderRadius: 6, border: '1px solid rgba(0,0,0,0.15)',
          resize: 'none', outline: 'none', marginBottom: 8, boxSizing: 'border-box',
        }}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onCommit(); } }}
      />
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{
          fontSize: 11, padding: '4px 10px', borderRadius: 6,
          border: '1px solid rgba(0,0,0,0.1)', background: '#fff', cursor: 'pointer', color: '#5a5a6e',
        }}>Cancel</button>
        <button onClick={onCommit} style={{
          fontSize: 11, padding: '4px 10px', borderRadius: 6,
          border: 'none', background: '#b07d1a', color: '#fff', cursor: 'pointer', fontWeight: 600,
        }}>Save</button>
      </div>
    </div>
  );
}

// ── Drill-level label ─────────────────────────────────────────────────────────
// Shown above the chart as context: e.g. "Monthly → March 2025 → Weekly"

// ── Main component ────────────────────────────────────────────────────────────

export default function DashboardChart({
  chartId, title, subtitle,
  data: staticData,
  queryFn, baseParams = {},
  xDataKey, series, height = 260,
  config = {}, isLoading: externalLoading, className = '',
  defaultChartType, onBarClick,
}: DashboardChartProps) {
  const navigate     = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  // Context
  const { chartTypes, setChartType, sortConfigs, setSortConfig, topNs, setTopN, features, toggleFeature } = useDashboard();
  const chartType = chartTypes[chartId] || defaultChartType || 'area';
  const sortBy    = sortConfigs[chartId] || 'value-desc';
  const topN      = topNs[chartId] ?? null;
  const feat      = features[chartId] || { trendLine: false, forecast: false, benchmark: false, brush: false, comparison: false };

  // Feature hooks
  const { path: drillPath, drillDown, drillUp, reset: drillReset, currentFilter } = useDrillDown(chartId);
  const { activeFilter, applyFilter, isSource } = useCrossFilter(chartId);
  const ann  = useAnnotations(chartId);
  const { exportPNG, exportCSV, copyShareLink, copied } = useExport(containerRef, staticData || [], chartId);

  const [annotationMode, setAnnotationMode] = useState(false);
  const [fullscreen,     setFullscreen    ] = useState(false);

  // ── Internal data fetch (for drill-down) ────────────────────────────────────
  const drillParams = { ...baseParams, ...currentFilter };
  const { data: fetchedData, isFetching } = useQuery({
    queryKey: [chartId, 'drill', drillParams],
    queryFn:  () => queryFn!(drillParams),
    enabled:  !!queryFn,
    staleTime: 30000,
  });

  // Resolved data: prefer fetched (drill) over static prop
  const rawData: Record<string, unknown>[] = useMemo(() => {
    const source = queryFn ? (fetchedData ?? []) : (staticData ?? []);
    return source.map((r) => ({ ...r, label: r.label ?? r.month ?? '' }));
  }, [queryFn, fetchedData, staticData]);

  const isLoading = externalLoading || isFetching;

  // Forecast / trendline
  const yKeys = series.map((s) => s.key);
  const { forecastData } = useForecast(rawData, yKeys, feat.forecast || feat.trendLine, 3);

  // Sort + top-N
  const primaryKey = series[0]?.key ?? '';
  const processedData = useMemo(() => {
    let d = forecastData;
    d = applySort(d, sortBy, primaryKey);
    d = applyTopN(d, topN, primaryKey);
    return d;
  }, [forecastData, sortBy, topN, primaryKey]);

  // ── Click handler ────────────────────────────────────────────────────────────
  const handleClick = useCallback((payload: Record<string, unknown>) => {
    const xVal = String(payload[xDataKey] ?? '');

    // Annotation mode
    if (annotationMode) { ann.startDraft(xVal, primaryKey); return; }

    // Drill down: push next level if drillLevels configured
    if (config.enableDrillDown && config.drillLevels) {
      const nextLevelIdx = drillPath.length;
      if (nextLevelIdx < config.drillLevels.length) {
        const level = config.drillLevels[nextLevelIdx];
        drillDown(xVal, { [level.paramKey]: xVal });
        return;
      }
    }

    // Cross-filter other charts
    if (config.enableCrossFilter !== false) {
      applyFilter(xDataKey, xVal, `${xVal}: ${Number(payload[primaryKey] ?? 0)}`);
    }

    // Navigate to linked module page
    if (config.linkedPage && config.enableLinkedPage !== false) {
      navigate(`${config.linkedPage}?filter=${encodeURIComponent(xVal)}`);
    }
  }, [annotationMode, xDataKey, primaryKey, config, drillPath, drillDown, applyFilter, navigate, ann]);

  // ── Recharts shared elements ─────────────────────────────────────────────────
  const commonProps = {
    data: processedData,
    margin: { top: 6, right: 12, bottom: 4, left: 0 },
    onClick: (d: { activePayload?: { payload: Record<string, unknown> }[] }) => {
      if (d?.activePayload?.[0]?.payload) handleClick(d.activePayload[0].payload);
    },
    style: { cursor: config.enableDrillDown ? 'zoom-in' : 'default' },
  };

  const CustomTooltip = (props: {
    active?: boolean;
    payload?: { name: string; value: number; color: string; dataKey: string }[];
    label?: string;
  }) => <EnrichedTooltip {...props} series={series} allData={rawData} xDataKey={xDataKey} />;

  const sharedInner = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#C6D8D8" strokeOpacity={0.4} vertical={false} />
      <XAxis dataKey={xDataKey} tick={{ fontSize: 10, fill: '#6899A0' }} />
      <YAxis tick={{ fontSize: 10, fill: '#6899A0' }} width={30} />
      <Tooltip content={<CustomTooltip />} />
      <Legend wrapperStyle={{ fontSize: 11 }} />
      {feat.benchmark && config.benchmarkValue !== undefined && (
        <ReferenceLine y={config.benchmarkValue} stroke="#F59E0B"
          strokeDasharray="6 3" strokeWidth={1.5}
          label={{ value: config.benchmarkLabel ?? 'Target', position: 'right', fontSize: 10, fill: '#F59E0B' }} />
      )}
      {feat.brush && (
        <Brush dataKey={xDataKey} height={20} stroke="#b07d1a" travellerWidth={6} />
      )}
      {ann.annotations.map((a) => (
        <ReferenceLine key={a.id} x={a.xValue} stroke="#7C3AED"
          strokeDasharray="4 2" strokeWidth={1.5}
          label={{ value: `📌 ${a.text.slice(0, 18)}…`, position: 'insideTopLeft', fontSize: 9, fill: '#7C3AED' }} />
      ))}
    </>
  );

  // ── Chart type renderers ─────────────────────────────────────────────────────
  function renderChartContent(type: ChartType, h: number) {
    if (type === 'table') {
      return <DataTableView data={processedData} xDataKey={xDataKey} series={series} />;
    }

    if (type === 'bar') {
      return (
        <ResponsiveContainer width="100%" height={h}>
          <BarChart {...commonProps}>
            {sharedInner}
            {series.map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color}
                radius={[2, 2, 0, 0]} maxBarSize={24} opacity={isSource ? 0.4 : 1}
                onClick={onBarClick ? (data, index) => onBarClick(data, index) : undefined}
                style={onBarClick ? { cursor: 'pointer' } : undefined}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (type === 'line') {
      return (
        <ResponsiveContainer width="100%" height={h}>
          <LineChart {...commonProps}>
            {sharedInner}
            {series.map((s) => (
              <React.Fragment key={s.key}>
                <Line type="monotone" dataKey={s.key} name={s.name}
                  stroke={s.color} strokeWidth={2} dot={false} opacity={isSource ? 0.4 : 1} />
                {feat.trendLine && (
                  <Line type="monotone" dataKey={`${s.key}_trend`}
                    stroke={s.color} strokeDasharray="4 2" strokeWidth={1.5}
                    dot={false} strokeOpacity={0.6} legendType="none" />
                )}
                {feat.forecast && (
                  <Line type="monotone" dataKey={`${s.key}_forecast`}
                    stroke={s.color} strokeDasharray="6 3" strokeWidth={1.5}
                    dot={false} legendType="none" />
                )}
              </React.Fragment>
            ))}
          </LineChart>
        </ResponsiveContainer>
      );
    }

    // Default: area
    return (
      <ResponsiveContainer width="100%" height={h}>
        <AreaChart {...commonProps}>
          <defs>
            {series.map((s) => (
              <linearGradient key={s.key} id={`g-${chartId}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={s.color} stopOpacity={0.18} />
                <stop offset="95%" stopColor={s.color} stopOpacity={0}    />
              </linearGradient>
            ))}
          </defs>
          {sharedInner}
          {series.map((s) => (
            <React.Fragment key={s.key}>
              <Area type="monotone" dataKey={s.key} name={s.name}
                stroke={s.color} fill={`url(#g-${chartId}-${s.key})`}
                strokeWidth={2} dot={false} opacity={isSource ? 0.4 : 1} />
              {feat.trendLine && (
                <Line type="monotone" dataKey={`${s.key}_trend`}
                  stroke={s.color} strokeDasharray="4 2" strokeWidth={1.5}
                  dot={false} strokeOpacity={0.6} legendType="none" />
              )}
              {feat.forecast && (
                <Area type="monotone" dataKey={`${s.key}_forecast`}
                  stroke={s.color} strokeDasharray="6 3"
                  fill={s.color} fillOpacity={0.05}
                  strokeWidth={1.5} dot={false} legendType="none" />
              )}
            </React.Fragment>
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  // ── Card inner content ────────────────────────────────────────────────────────
  const cardInner = (h: number) => (
    <div style={{ position: 'relative' }}>
      {/* Cross-filter badge */}
      {activeFilter && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
          padding: '3px 8px', background: '#F0F9FF',
          borderRadius: 6, border: '1px solid #BAE6FD', fontSize: 10.5, color: '#0284C7',
        }}>
          <span style={{ fontWeight: 600 }}>Filtered: {activeFilter.label}</span>
          <span style={{ flex: 1 }} />
          <button onClick={() => applyFilter(activeFilter.field, activeFilter.value, activeFilter.label)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', lineHeight: 1 }}>
            <X size={10} />
          </button>
        </div>
      )}

      {/* Drill breadcrumb */}
      <DrillBreadcrumb path={drillPath} onUp={drillUp} onReset={drillReset} />

      {/* Drill level hint */}
      {config.enableDrillDown && config.drillLevels && drillPath.length < config.drillLevels.length && (
        <div style={{
          fontSize: 10, color: '#94A3B8', marginBottom: 4, fontStyle: 'italic',
        }}>
          Click any bar to drill into {config.drillLevels[drillPath.length]?.label ?? 'next level'}
        </div>
      )}

      {/* Toolbar */}
      <ChartToolbar
        chartId={chartId}
        chartType={chartType}
        onChartType={(t) => setChartType(chartId, t)}
        trendLine={feat.trendLine}
        forecast={feat.forecast}
        benchmark={feat.benchmark}
        brush={feat.brush}
        onToggle={(f) => toggleFeature(chartId, f)}
        sortBy={sortBy}
        onSort={(s) => setSortConfig(chartId, s)}
        topN={topN}
        onTopN={(n) => setTopN(chartId, n)}
        onExportPNG={exportPNG}
        onExportCSV={exportCSV}
        onShareLink={copyShareLink}
        shareCopied={copied}
        onFullscreen={() => setFullscreen(true)}
        annotationMode={annotationMode}
        onAnnotationMode={setAnnotationMode}
        annotationCount={ann.annotations.length}
        enableForecast={config.enableForecast !== false}
        enableAnnotations={config.enableAnnotations !== false}
        enableBenchmark={config.enableBenchmark !== false}
        enableBrush={config.enableBrush !== false}
      />

      {/* Annotation mode hint */}
      {annotationMode && (
        <div style={{
          fontSize: 10.5, color: '#7C3AED', background: '#F5F3FF',
          border: '1px solid #DDD6FE', borderRadius: 6,
          padding: '3px 10px', marginBottom: 4,
        }}>
          Click a data point to pin a note. Press Esc or toggle to exit.
        </div>
      )}

      {/* Chart or skeleton */}
      <div style={{ position: 'relative' }}>
        {isLoading ? (
          <div style={{
            height: h, background: '#f7f5f2', borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              border: '3px solid rgba(0,0,0,0.1)', borderTopColor: '#b07d1a',
              animation: 'spin 0.8s linear infinite',
            }} />
          </div>
        ) : (
          renderChartContent(chartType, h)
        )}

        {/* Annotation draft input */}
        {ann.drafting && (
          <AnnotationInput
            xValue={ann.drafting.xValue}
            text={ann.draftText}
            onChange={ann.setDraftText}
            onCommit={ann.commitDraft}
            onCancel={ann.cancelDraft}
          />
        )}
      </div>

      {/* Pinned annotation chips */}
      {ann.annotations.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {ann.annotations.map((a) => (
            <div key={a.id} style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '2px 7px', background: '#F5F3FF',
              border: '1px solid #DDD6FE', borderRadius: 100, fontSize: 10, color: '#7C3AED',
            }}>
              📌 <strong>{a.xValue}</strong>: {a.text}
              <button onClick={() => ann.remove(a.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', lineHeight: 1, padding: 0 }}>
                <X size={9} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Fullscreen modal ──────────────────────────────────────────────────────────
  const Fullscreen = fullscreen && (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}
      onClick={(e) => { if (e.target === e.currentTarget) setFullscreen(false); }}>
      <div style={{
        background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderRadius: 4, padding: '20px 24px',
        width: '95vw', maxWidth: 1200,
        boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', margin: 0 }}>{title}</h3>
            {subtitle && <p style={{ fontSize: 12, color: '#5a5a6e', margin: '2px 0 0' }}>{subtitle}</p>}
          </div>
          <button onClick={() => setFullscreen(false)} style={{
            background: 'rgba(0,0,0,0.04)', border: 'none', borderRadius: 8,
            width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={14} color="#5a5a6e" />
          </button>
        </div>
        {cardInner(480)}
      </div>
    </div>
  );

  // ── Card ──────────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        ref={containerRef}
        className={`card p-4 ${className}`}
        style={{ position: 'relative' }}
        tabIndex={-1}
        onKeyDown={(e) => { if (e.key === 'Escape') { setAnnotationMode(false); setFullscreen(false); } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 2 }}>
          <div>
            <h3 className="font-serif font-semibold text-sm text-text">{title}</h3>
            {subtitle && <p className="text-xs text-text-muted mt-0.5">{subtitle}</p>}
          </div>
          {isSource && (
            <span style={{
              fontSize: 9.5, fontWeight: 700, padding: '2px 7px', borderRadius: 100,
              background: '#FEF9C3', color: '#CA8A04', border: '1px solid #FDE04740',
            }}>Cross-filtering</span>
          )}
        </div>
        {cardInner(height)}
      </div>
      {Fullscreen}
    </>
  );
}
